import { type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { type ElementType } from 'react';

import { typographyVariantStyles } from '../../typography-variants';
import { TypographyProps, TypographyVariant } from './types';

export { typographyVariantStyles };

const defaultElements: Record<TypographyVariant, keyof React.JSX.IntrinsicElements> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  body1: 'p',
  body2: 'p',
  body3: 'p',
  body4: 'p',
  body5: 'p',
};

const BLOCKED_PROPS = new Set(['$variant', '$noWrap', '$align', '$fontWeight', '$color']);

interface RootStyleProps {
  $variant: TypographyVariant;
  $noWrap?: boolean;
  $align?: React.CSSProperties['textAlign'];
  $fontWeight?: React.CSSProperties['fontWeight'];
  $color?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolyRoot = ({ $tag: Tag = 'p', ...rest }: { $tag?: ElementType } & any) => <Tag {...rest} />;

const Root = styled(PolyRoot, {
  shouldForwardProp: (prop) => typeof prop === 'string' && !BLOCKED_PROPS.has(prop),
})<RootStyleProps>(
  ({
    $variant,
    $noWrap,
    $align,
    $fontWeight,
    $color,
    theme,
  }: RootStyleProps & { theme?: Theme }) => ({
    margin: 0,
    padding: 0,
    color: $color ?? theme?.colors.white[100],
    ...typographyVariantStyles[$variant],
    ...($align && { textAlign: $align }),
    ...($fontWeight !== undefined && { fontWeight: $fontWeight }),
    ...($noWrap && { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  }),
);

function Typography<C extends ElementType = 'p'>({
  as,
  variant = 'body2',
  align,
  fontWeight,
  color,
  noWrap,
  ...rest
}: TypographyProps<C>) {
  return (
    <Root
      $tag={as ?? defaultElements[variant as TypographyVariant]}
      $variant={variant}
      $noWrap={noWrap}
      $align={align}
      $fontWeight={fontWeight}
      $color={color}
      {...rest}
    />
  );
}

Typography.displayName = 'Typography';

export default Typography;
